import React, { useEffect, useState } from "react";
import Select from "react-select";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import { showError } from "utils/notifications";
import FormTextarea from "Common/Custom/FormTextarea";
import { NavLink } from "react-router-dom";

const AddPurchaseItemForm = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  loading,
  supplierOptions = [],
  productOptions = [],
  title = "Add Purchase Item",
  fields = [],
}) => {
  const [supplier, setSupplier] = useState(values.supplier_id || "");
  const [items, setItems] = useState([
    { item_name: null, stock: "", quantity: "", rate: "", total: "" },
  ]);

  const isSupplierSelected = !!supplier;

  // supplier dropdown
  const optionsSupplier = supplierOptions.map((opt) => ({
    value: opt.supplier_id,
    label: opt.supplier_name,
  }));

  // product dropdown
  const optionsProduct = productOptions.map((opt) => ({
    value: opt.id,
    label: `${opt.product_name} (Stock: ${opt.stock})`,
    stock: opt.stock,
  }));

  const handleSupplierChange = (e) => {
    const { value } = e.target;
    setSupplier(value);
    handleChange(e);
  };

  const handleAddItem = () => {
    setItems([...items, { item_name: null, stock: "", quantity: "", rate: "", total: "" }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    const item = updated[index];

    if (field === "item_name") {
      const product = productOptions.find((p) => p.id === value?.value);
      item.item_name = value;
      item.stock = product ? product.stock : "";
      item.quantity = "";
      item.rate = "";
      item.total = "";
    } else if (field === "quantity") {
      const qty = parseFloat(value) || "";
      item.quantity = qty;
      item.total = qty !== "" ? qty * (parseFloat(item.rate) || 0) : "";
    } else if (field === "rate") {
      const rate = parseFloat(value) || "";
      item.rate = rate;
      item.total = rate !== "" ? rate * (parseFloat(item.quantity) || 0) : "";
    }

    setItems(updated);
  };

  const grandTotal = items.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

  // ✅ Prepare clean data — only valid items (with product_id, quantity > 0, rate > 0)
  const prepareData = () => {
    const formattedItems = items
      .filter(
        (i) =>
          i.item_name &&
          i.item_name.value &&
          parseFloat(i.quantity) > 0 &&
          parseFloat(i.rate) > 0
      )
      .map((i) => ({
        product_id: i.item_name.value,
        quantity: parseFloat(i.quantity),
        rate: parseFloat(i.rate),
        total: parseFloat(i.total),
      }));

    return {
      ...values,
      supplier_id: supplier,
      items: formattedItems,
      grand_total: formattedItems.reduce(
        (sum, item) => sum + (item.total || 0),
        0
      ),
    };
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const prepared = prepareData();

    if (!prepared.items || prepared.items.length === 0) {
      showError("Please add at least one valid item before submitting.");
      return;
    }

    handleSubmit(null, prepared);
  };


  const getAvailableOptions = (currentIndex) => {
    const selectedProductIds = items
      .map((it, idx) => (idx !== currentIndex ? it.item_name?.value : null))
      .filter(Boolean);
    return optionsProduct.filter((opt) => !selectedProductIds.includes(opt.value));
  };

  const [noOptions, setNoOptions] = useState(false);

  const availableOptions = getAvailableOptions();

  useEffect(() => {
    setNoOptions(availableOptions.length === 0);
  }, [availableOptions.length]);

  return (
    <PageBody
      onSubmit={handleFormSubmit}
      headerchildren={
        <>
          <HeadingOne>{title}</HeadingOne>
          <NavLink to='/admin/items-suppliers-list/add-supplier' className="btn btn-primary">Create Supplier</NavLink>
        </>
      }
    >

      {fields.map((field) => {

        if (field.type === "select" && field.name === "supplier_id") {
          return (
            <FormSelect
              key={field.name}
              field={field}
              value={supplier}
              handleChange={handleSupplierChange}
              options={optionsSupplier}
              error={errors[field.name]}
            />
          );
        }

        if (field.type === "date") {
          const isExpiry = field.name === "purchase_expiry_date";

          return (
            <FormDatePicker
              key={field.name}
              field={{
                ...field,
                minDate: isExpiry ? values.purchase_date : null
              }}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
              dateFormat="Y-m-d"
              disabled={!isSupplierSelected}
            />
          );
        }


        if (field.type === "textarea") {
          return (
            <FormTextarea
              key={field.name}
              field={field}
              value={values[field.name]}
              disabled
              error={errors[field.name]}
              handleChange={handleChange}
            />
          );
        }

        return (
          <FormInput
            key={field.name}
            field={field}
            value={values[field.name]}
            handleChange={handleChange}
            error={errors[field.name]}
            disabled={!isSupplierSelected}
          />
        );
      })}

      {/* Items Table */}
      <div className="col-12">
        <div className="d-flex justify-content-end align-items-center my-3">
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleAddItem}
            disabled={!isSupplierSelected || noOptions}
          >
            Add More Item
          </button>
        </div>
        <table className="table table-bordered align-middle ">
          <thead className="table-light">
            <tr>
              <th>Item Information</th>
              <th>Stock/Qnt</th>
              <th>Quantity</th>
              <th>Rate</th>
              <th>Total</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody >
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="Select Item"
                    value={item.item_name}
                    onChange={(selected) => handleItemChange(index, "item_name", selected)}
                    options={getAvailableOptions(index)}
                    isClearable
                    isDisabled={!isSupplierSelected}
                  />
                </td>

                <td>
                  <input type="number" placeholder="0" className="form-control text-end" value={item.stock} readOnly disabled />
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control text-end"
                    placeholder="0"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                    disabled={!item.item_name || !isSupplierSelected}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    placeholder="0"
                    className="form-control text-end"
                    value={item.rate}
                    onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                    disabled={!item.item_name || !isSupplierSelected}
                  />
                </td>

                <td>
                  <input type="number" placeholder="0" className="form-control text-end" value={item.total} readOnly disabled />
                </td>

                <td className="text-center">
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm rounded-circle"
                      onClick={() => handleRemoveItem(index)}
                      disabled={!isSupplierSelected}
                    >
                      <i class="bi bi-x fs-6"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-end fw-bold">Grand Total: {grandTotal.toFixed(2)}</div>
      </div>

      {/* Submit */}
      <FormSubmitButton
        label="Submit"
        loading={loading}
        colSize="col-12"
        align="end"
        disabled={!isSupplierSelected}
      />
    </PageBody>
  );
};

export default AddPurchaseItemForm;
